/**
 * Cvent Integration Service (Client)
 * 
 * API client for Cvent RFP integration endpoints
 */

import { apiFetch } from '@/lib/api';
import type { EventDTO } from './EventsService';
import type { Venue } from './VenuesService';

/**
 * Cvent RFP from REST API
 */
export interface CventRfp {
  id: string;
  name: string;
  code?: string;
  type?: string;
  status?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  decisionDate?: string;
  responseDueDate?: string;
  organizationName?: string;
  [key: string]: any; // REST API returns many additional fields
}

/**
 * Cvent RFP search result from REST API
 */
export interface CventRfpListResponse {
  rfps: CventRfp[];
  totalCount: number;
  fetchedCount: number;
}

/**
 * Cvent venue summary
 */
export interface CventVenueSummary {
  id: string;
  code: string;
  venueName: string;
  /** Cvent response status (e.g. AWAITING_PROPOSAL, SUBMITTED_PROPOSAL). */
  status?: string | null;
  brand?: string | null;
  chain?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    stateProvince?: string;
    countryCode?: string;
    postalCode?: string;
  } | null;
  website?: string | null;
  totalSleepingRoom?: number | null;
}

/**
 * Match signals from venue matcher
 */
export interface MatchSignals {
  name: number; // 0-100
  location: number; // 0-100
  brand: number; // 0-100
  geolocation: number | null; // 0-100 or null
  website: number | null; // 0-100 or null
  roomCount: number | null; // 0-100 or null
}

/**
 * Shortlist item with Cvent data
 */
export interface ShortlistItemWithCvent {
  id: string;
  research_destination_id: string;
  venue_id: string;
  cvent_supplier_id: string | null;
  cvent_match_confidence: number | null;
  cvent_match_signals: MatchSignals | null;
  venue: Venue;
}

/**
 * Matched venue result
 */
export interface MatchedVenue {
  shortlistItem: ShortlistItemWithCvent;
  cventVenue: CventVenueSummary;
  confidence: number;
  signals: MatchSignals;
  distanceKm?: number;
}

/**
 * Venue in DB but not shortlisted
 */
export interface InDbVenue {
  venue: Venue;
  cventVenue: CventVenueSummary;
  confidence: number;
  signals: MatchSignals;
  distanceKm?: number;
}

/**
 * Venue matching results
 */
export interface VenueMatchResults {
  matched: MatchedVenue[];
  inDbNotShortlisted: InDbVenue[];
  notInDb: CventVenueSummary[];
}

/**
 * Connect event response
 */
export interface ConnectEventResponse {
  event: EventDTO;
  matchResults: VenueMatchResults;
}

/**
 * Venue matches response
 */
export interface VenueMatchesResponse {
  rfpId: string | null;
  rfpName: string | null;
  rfpCode: string | null;
  matchResults: VenueMatchResults;
}

/**
 * Cvent Integration Service
 */
export class CventIntegrationService {
  /**
   * Search Cvent RFPs
   */
  async searchRfps(params: {
    searchString?: string;
    limit?: number;
    statuses?: string[];
  }): Promise<CventRfpListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.searchString) {
      queryParams.append('searchString', params.searchString);
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => {
        queryParams.append('statuses', status);
      });
    }

    const url = `/api/cvent/rfps${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    try {
      const res = await apiFetch(url);
      
      if (!res.ok) {
        // Try to parse error response
        let errorMessage = 'Failed to search Cvent RFPs';
        let errorCode: string | undefined;
        try {
          const errorData = await res.json();
          if (errorData?.error?.message) {
            errorMessage = errorData.error.message;
          }
          if (errorData?.error?.code) {
            errorCode = errorData.error.code;
          }
        } catch {
          // JSON parse failed, use status-based message
          if (res.status === 502) {
            errorMessage = 'Cvent service unavailable. Please check your browser cookies in Admin Settings.';
            errorCode = 'EXTERNAL_SERVICE_ERROR';
          } else if (res.status === 401) {
            errorMessage = 'Cvent authentication required. Please update browser cookies in Admin Settings.';
            errorCode = 'AUTH_ERROR';
          }
        }
        
        // Create error with status code attached
        const error = new Error(errorMessage) as Error & { statusCode?: number; code?: string };
        error.statusCode = res.status;
        error.code = errorCode;
        throw error;
      }
      
      const data = await res.json();
      return data.data as CventRfpListResponse; // { rfps, totalCount, fetchedCount }
    } catch (error) {
      // Re-throw with original message and status code if it's already an Error
      if (error instanceof Error) {
        throw error;
      }
      const err = new Error('Failed to search Cvent RFPs') as Error & { statusCode?: number };
      err.statusCode = 500;
      throw err;
    }
  }

  /**
   * Connect event to Cvent RFP
   */
  async connectEventToRfp(
    eventId: string,
    rfpId: string
  ): Promise<ConnectEventResponse> {
    const res = await apiFetch(`/api/events/${eventId}/cvent/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rfpId }),
    });
    
    if (!res.ok) {
      throw new Error('Failed to connect event to Cvent RFP');
    }
    
    const data = await res.json();
    return data.data as ConnectEventResponse;
  }

  /**
   * Disconnect event from Cvent RFP
   */
  async disconnectEventFromRfp(eventId: string): Promise<{ message: string; removedConnections: number }> {
    const res = await apiFetch(`/api/events/${eventId}/cvent/disconnect`, {
      method: 'DELETE',
    });
    
    if (!res.ok) {
      throw new Error('Failed to disconnect event from Cvent RFP');
    }
    
    const data = await res.json();
    return data.data as { message: string; removedConnections: number };
  }

  /**
   * Get venue matching results
   * Uses cached data if available (refreshes if older than 30 minutes)
   */
  async getVenueMatches(eventId: string): Promise<VenueMatchesResponse> {
    const res = await apiFetch(`/api/events/${eventId}/cvent/venue-matches`);
    
    if (!res.ok) {
      throw new Error('Failed to get venue matches');
    }
    
    const data = await res.json();
    return data.data as VenueMatchesResponse;
  }

  /**
   * Refresh Cvent data manually (forces fresh fetch from API, bypasses cache)
   */
  async refreshCventData(eventId: string): Promise<VenueMatchesResponse> {
    const res = await apiFetch(`/api/events/${eventId}/cvent/refresh-data`, {
      method: 'POST',
    });
    
    if (!res.ok) {
      throw new Error('Failed to refresh Cvent data');
    }
    
    const data = await res.json();
    return data.data as VenueMatchesResponse;
  }

  /**
   * Add existing venue to shortlist (venue in DB)
   * Automatically assigns venue to destination based on its city
   */
  async addVenueToShortlist(params: {
    eventId: string;
    venueId: string;
    cventSupplierId: string;
  }): Promise<{ shortlistId: string }> {
    const res = await apiFetch(`/api/events/${params.eventId}/shortlist/add-from-cvent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        venueId: params.venueId,
        cventSupplierId: params.cventSupplierId,
      }),
    });
    
    if (!res.ok) {
      throw new Error('Failed to add venue to shortlist');
    }
    
    const data = await res.json();
    return data.data as { shortlistId: string };
  }

  /**
   * Preview Cvent venue import - check if destination selection is needed
   * Returns geocoded location and available destinations without creating anything
   */
  async previewCventVenueImport(params: {
    eventId: string;
    cventSupplierId: string;
  }): Promise<{
    venueName: string;
    geocodedLocation: { city: string | null; country: string | null; state: string | null };
    matchedDestination: { id: string; city: string; country: string } | null;
    availableDestinations: Array<{ id: string; city: string; country: string }>;
    requiresSelection: boolean;
  }> {
    const res = await apiFetch('/api/venues/preview-cvent-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventId: params.eventId,
        cventSupplierId: params.cventSupplierId,
      }),
    });
    
    if (!res.ok) {
      throw new Error('Failed to preview Cvent venue import');
    }
    
    const data = await res.json();
    return data.data;
  }

  /**
   * Create venue from Cvent and add to shortlist
   * If destinationId is provided, uses that destination; otherwise auto-creates based on geocoded city
   */
  async createVenueFromCvent(params: {
    eventId: string;
    cventSupplierId: string;
    destinationId?: string; // Optional: if provided, use this destination
  }): Promise<{ venueId: string; shortlistId: string; destinationId: string }> {
    const res = await apiFetch('/api/venues/create-from-cvent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventId: params.eventId,
        cventSupplierId: params.cventSupplierId,
        destinationId: params.destinationId,
      }),
    });
    
    if (!res.ok) {
      throw new Error('Failed to create venue from Cvent');
    }
    
    const data = await res.json();
    return data.data as { venueId: string; shortlistId: string; destinationId: string };
  }

  /**
   * Disconnect a single venue from Cvent supplier
   * This allows re-matching the venue if a mistake was made
   */
  async disconnectVenueFromCvent(params: {
    eventId: string;
    shortlistId: string;
  }): Promise<{ message: string }> {
    const res = await apiFetch(`/api/events/${params.eventId}/shortlist/${params.shortlistId}/cvent/disconnect`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      throw new Error('Failed to disconnect venue from Cvent');
    }

    const data = await res.json();
    return data.data as { message: string };
  }

  /**
   * Manually connect a Moniker shortlist row to a specific Cvent supplier.
   * Escape hatch for when the auto-matcher couldn't bridge them (different
   * names / coordinates / place_ids between Cvent and our DB).
   */
  async manuallyConnectShortlist(params: {
    eventId: string;
    shortlistId: string;
    cventSupplierId: string;
  }): Promise<{ cventVenueName: string; cventStatus: string | null }> {
    const res = await apiFetch(
      `/api/events/${params.eventId}/shortlist/${params.shortlistId}/cvent/connect`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cventSupplierId: params.cventSupplierId }),
      }
    );

    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      const message = errBody?.error?.message || 'Failed to connect to Cvent supplier';
      throw new Error(message);
    }

    const data = await res.json();
    return data.data as { cventVenueName: string; cventStatus: string | null };
  }
}

